import { Pipe, PipeTransform } from "@angular/core";

@Pipe({
  name: 'highlight'
})
export class HighlightSearch implements PipeTransform {
  constructor() { }

  transform(value: any, args: any): any {
    if (!args || !value) return value;

    const searchQuery = this.normalizeInput(args);
    if (!searchQuery) return value;

    // Extract HTML tags and replace with placeholders
    const tagMap = new Map<string, string>();
    let textWithPlaceholders = this.extractHtmlTags(value, tagMap);

    // Perform highlighting on clean text
    let highlighted = this.highlightText(textWithPlaceholders, searchQuery);

    // Restore HTML tags
    let result = this.restoreHtmlTags(highlighted, tagMap);

    return result;
  }

  /**
   * Normalize the search input by decoding URI components and removing quotes
   */
  private normalizeInput(input: any): string {
    let normalized: string;
    
    if (typeof input === 'string') {
      try {
        normalized = decodeURIComponent(input);
      } catch {
        normalized = input;
      }
    } else {
      normalized = String(input);
    }

    return normalized.replaceAll(/['"]/g, '').trim();
  }

  /**
   * Extract all HTML tags and replace them with unique placeholders
   */
  private extractHtmlTags(html: string, tagMap: Map<string, string>): string {
    let counter = 0;
    return html.replace(/<[^>]+>/g, (tag) => {
      // Private-use chars so a search term can never match inside a placeholder
      const placeholder = `\uE000${counter}\uE001`;
      tagMap.set(placeholder, tag);
      counter++;
      return placeholder;
    });
  }

  /**
   * Restore HTML tags from placeholders
   */
  private restoreHtmlTags(text: string, tagMap: Map<string, string>): string {
    let result = text;
    tagMap.forEach((tag, placeholder) => {
      result = result.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), tag);
    });
    return result;
  }

  /**
   * Main highlighting logic with Arabic support
   */
  private highlightText(text: string, searchQuery: string): string {
    const arabicVariations = [
      { a: "ا", b: "أ" }, { a: "ا", b: "إ" }, { a: "ا", b: "ى" },
      { a: "أ", b: "ى" }, { a: "أ", b: "إ" }, { a: "إ", b: "ى" },
      { a: "ه", b: "ة" }, { a: "و", b: "ؤ" }, { a: "ت", b: "ة" },
      { a: "ض", b: "ظ" }, { a: "ئ", b: "ء" },
    ];

    // Split search query into terms (single letters would highlight everywhere)
    const searchTerms = searchQuery.split(/\s+/).filter(term => term.length > 1);

    // Collect every term variation and match them all in one pass, longest first,
    // so already-inserted <mark> tags are never re-scanned or double-wrapped
    const allVariations = new Set<string>();
    searchTerms.forEach((term) => {
      this.generateArabicVariations(term, arabicVariations).forEach(v => {
        if (v.length > 1) allVariations.add(v);
      });
    });
    if (allVariations.size === 0) return text;

    const pattern = Array.from(allVariations)
      .sort((a, b) => b.length - a.length)
      .map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');

    // No \b word boundaries: in JS they only recognise ASCII letters, so they
    // never match around Arabic words
    return text.replace(new RegExp(pattern, 'gi'), (match) => `<mark class='highlightedtext'>${match}</mark>`);
  }

  /**
   * Generate Arabic character variations for a given term
   */
  private generateArabicVariations(term: string, variations: Array<{ a: string; b: string }>): Set<string> {
    const termVariations = new Set<string>();
    
    // Add the original term
    termVariations.add(term);

    // Generate variations by replacing Arabic characters
    variations.forEach((rel) => {
      const variant = term.replace(new RegExp(rel.a, 'g'), rel.b);
      if (variant !== term) {
        termVariations.add(variant);
      }
    });

    // Handle Arabic prefixes (الـ)
    if (term.startsWith("ال")) {
      termVariations.add(term.substring(2)); // Remove prefix
    } else {
      termVariations.add(`ال${term}`); // Add prefix
    }

    return termVariations;
  }

  /**
   * Utility to validate regex patterns
   */
  static makeValidRegExp(input: string): string {
    try {
      new RegExp(input);
      return input;
    } catch (error) {
      if (error instanceof SyntaxError) {
        return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }
      throw error;
    }
  }
}
